import type { ResumeDto } from "@reactive-resume/dto";
import { useState } from "react";
import { useNavigate } from "react-router";

import { useResumeDownload } from "@/client/services/resume/download-cta";
import { useResumeThumbnail } from "@/client/services/resume/preview";
import { useDialog } from "@/client/stores/dialog";

import { DocumentCard } from "../components/document-card";
import { ResumeDownloadDialog } from "../components/resume-download-dialog";
import type { DocumentAction, UserDocument } from "../types/document";

type ResumeCardProps = {
  resume: ResumeDto;
};

const convertResumeToDocument = (resume: ResumeDto, thumbnailUrl: string): UserDocument => ({
  id: resume.id,
  kind: "resume",
  title: resume.title,
  thumbnailUrl,
  createdAt: String(resume.createdAt),
  updatedAt: String(resume.updatedAt),
});

export const ResumeCard = ({ resume }: ResumeCardProps) => {
  const navigate = useNavigate();

  const resumeDialog = useDialog<ResumeDto>("resume");

  const { handleDownload, loading: downloadLoading } = useResumeDownload();

  const thumbnail = useResumeThumbnail(resume.id, resume.updatedAt, resume.data.metadata.template);

  const [showConfirm, setShowConfirm] = useState(false);

  const document = convertResumeToDocument(resume, thumbnail.src);

  const openResume = () => {
    void navigate(`/client/builder/${resume.id}`);
  };

  const handleAction = (_document: UserDocument, action: DocumentAction) => {
    switch (action) {
      case "open": {
        openResume();
        break;
      }

      case "rename": {
        resumeDialog.open("update", {
          id: "resume",
          item: resume,
        });

        break;
      }

      case "duplicate": {
        resumeDialog.open("duplicate", {
          id: "resume",
          item: resume,
        });

        break;
      }

      case "download": {
        if (!downloadLoading) {
          setShowConfirm(true);
        }

        break;
      }

      case "delete": {
        resumeDialog.open("delete", {
          id: "resume",
          item: resume,
        });

        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <>
      <DocumentCard
        document={document}
        onOpen={openResume}
        onAction={handleAction}
        showDownloadCta
        isDownloading={downloadLoading}
        onThumbnailError={thumbnail.showTemplate}
      />

      <ResumeDownloadDialog
        resume={resume}
        open={showConfirm}
        loading={downloadLoading}
        onOpenChange={setShowConfirm}
        onConfirm={() => {
          void handleDownload(resume.id).then(() => {
            setShowConfirm(false);
          });
        }}
      />
    </>
  );
};
